import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Save, Trash2, Shield, Building2, Coins, ListTree, Users, Wallet, Heart } from "lucide-react";

const LIST_LABELS = {
  inventory_categories: "Categorie magazzino",
  income_categories: "Categorie entrate",
  expense_categories: "Categorie uscite",
  employee_roles: "Ruoli dipendenti",
  user_roles: "Ruoli utenti sistema",
  task_categories: "Categorie task",
  task_priorities: "Priorità task",
  task_statuses: "Stati task",
  order_statuses: "Stati ordine",
  checklist_categories: "Categorie checklist",
  competitor_types: "Tipi concorrenti",
  partnership_types: "Tipi partnership",
  partnership_statuses: "Stati partnership",
  campaign_kinds: "Tipi campagna",
  campaign_statuses: "Stati campagna",
  document_categories: "Categorie documenti",
  account_types: "Tipi conto",
  customer_kinds: "Tipi cliente",
  menu_categories: "Categorie menu",
  supplier_categories: "Categorie fornitori",
  menu_languages: "Lingue menu pubblico",
};

function ChipsEditor({ listKey, values, onSave }) {
  const [items, setItems] = useState(values || []);
  const [newVal, setNewVal] = useState("");
  useEffect(() => setItems(values || []), [values]);
  const add = () => {
    const v = newVal.trim();
    if (!v || items.includes(v)) { setNewVal(""); return; }
    setItems([...items, v]); setNewVal("");
  };
  const remove = (v) => setItems(items.filter((x) => x !== v));
  const changed = JSON.stringify(items) !== JSON.stringify(values || []);
  return (
    <div className="border border-[#E2D9C8] rounded-lg p-5 bg-white" data-testid={`list-${listKey}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-eyebrow">{LIST_LABELS[listKey] || listKey}</div>
          <div className="font-serif text-xl mt-1">{items.length} voci</div>
        </div>
        <Button size="sm" onClick={() => onSave(listKey, items)} disabled={!changed}
          data-testid={`save-list-${listKey}`}
          className={`${changed ? "bg-neutral-900 text-[#FDFBF7]" : ""}`}>
          <Save size={12} strokeWidth={1.5} /> Salva
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {items.map((v) => (
          <span key={v} className="inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded-full bg-[#F3EDE1] border border-[#E2D9C8]">
            {v}
            <button onClick={() => remove(v)} className="text-neutral-400 hover:text-red-700" data-testid={`remove-${listKey}-${v}`}>
              <X size={12} strokeWidth={1.5} />
            </button>
          </span>
        ))}
        {items.length === 0 && <span className="text-xs text-neutral-500">Nessuna voce</span>}
      </div>
      <div className="flex gap-2">
        <Input value={newVal} onChange={(e) => setNewVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Aggiungi voce…" className="max-w-xs" data-testid={`input-${listKey}`} />
        <Button size="sm" variant="outline" onClick={add} data-testid={`add-${listKey}`}><Plus size={12} strokeWidth={1.5} /> Aggiungi</Button>
      </div>
    </div>
  );
}

function BusinessTab({ settings, onSave }) {
  const [form, setForm] = useState(settings?.business || {});
  useEffect(() => setForm(settings?.business || {}), [settings]);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2"><Label>Nome attività</Label><Input data-testid="biz-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
      <div className="md:col-span-2"><Label>Tipologia</Label><Input value={form.tipology || ""} onChange={(e) => setForm({ ...form, tipology: e.target.value })} /></div>
      <div><Label>Città</Label><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
      <div><Label>Regione</Label><Input value={form.region || ""} onChange={(e) => setForm({ ...form, region: e.target.value })} /></div>
      <div><Label>Paese</Label><Input value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
      <div><Label>Coperti</Label><Input type="number" value={form.capacity || 0} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value || 0) })} /></div>
      <div><Label>Anno apertura</Label><Input type="number" value={form.opening_year || 0} onChange={(e) => setForm({ ...form, opening_year: parseInt(e.target.value || 0) })} /></div>
      <div><Label>Telefono</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
      <div><Label>Email</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
      <div className="md:col-span-2"><Label>Indirizzo</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
      <div className="md:col-span-2"><Label>Momenti servizio (virgola)</Label>
        <Input value={(form.service_moments || []).join(", ")}
          onChange={(e) => setForm({ ...form, service_moments: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
      </div>
      <div className="md:col-span-2">
        <Button data-testid="save-business" onClick={() => onSave(form)} className="bg-neutral-900 text-[#FDFBF7]"><Save size={14} strokeWidth={1.5} /> Salva configurazione</Button>
      </div>
    </div>
  );
}

function FinancialTab({ settings, onSave }) {
  const [form, setForm] = useState(settings?.financial || {});
  useEffect(() => setForm(settings?.financial || {}), [settings]);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div><Label>Valuta</Label><Input value={form.currency || ""} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></div>
      <div><Label>Soglia liquidità bassa (€)</Label><Input type="number" value={form.low_liquidity_threshold || 0} onChange={(e) => setForm({ ...form, low_liquidity_threshold: parseFloat(e.target.value || 0) })} /></div>
      <div><Label>IVA default cibo (%)</Label><Input type="number" value={form.default_vat_food || 0} onChange={(e) => setForm({ ...form, default_vat_food: parseInt(e.target.value || 0) })} /></div>
      <div><Label>IVA default bevande (%)</Label><Input type="number" value={form.default_vat_drink || 0} onChange={(e) => setForm({ ...form, default_vat_drink: parseInt(e.target.value || 0) })} /></div>
      <div><Label>Alert scadenza documenti (giorni)</Label><Input data-testid="fin-docs-alert" type="number" value={form.docs_expiring_alert_days ?? 30} onChange={(e) => setForm({ ...form, docs_expiring_alert_days: parseInt(e.target.value || 30) })} /></div>
      <div><Label>Obiettivo loyalty di default (stamp)</Label><Input data-testid="fin-loyalty-goal" type="number" min="1" value={form.loyalty_default_goal ?? 10} onChange={(e) => setForm({ ...form, loyalty_default_goal: parseInt(e.target.value || 10) })} /></div>
      <div className="md:col-span-2">
        <Button data-testid="save-financial" onClick={() => onSave(form)} className="bg-neutral-900 text-[#FDFBF7]"><Save size={14} strokeWidth={1.5} /> Salva parametri</Button>
      </div>
    </div>
  );
}

function CrmTab({ settings, onSave }) {
  const [form, setForm] = useState(settings?.crm || {});
  useEffect(() => setForm(settings?.crm || {}), [settings]);
  const num = (v, def) => (v === "" || v == null ? def : Number(v));
  return (
    <div className="space-y-6">
      <div className="text-sm text-neutral-500 border-l-2 border-[#D7C4A3] pl-4">
        Regole automatiche di segmentazione clienti. Modificando queste soglie i clienti verranno riclassificati immediatamente in <b>VIP</b>, <b>Abituali</b>, <b>Nuovi</b>, <b>Occasionali</b>, <b>Inattivi</b>.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-4 space-y-3">
          <div className="label-eyebrow text-amber-900">VIP</div>
          <div><Label>Visite minime</Label><Input data-testid="crm-vip-visits" type="number" value={form.vip_min_visits ?? 50} onChange={(e) => setForm({ ...form, vip_min_visits: num(e.target.value, 50) })} /></div>
          <div><Label>Spesa media minima (€)</Label><Input data-testid="crm-vip-avg" type="number" step="0.5" value={form.vip_min_avg_spend ?? 20} onChange={(e) => setForm({ ...form, vip_min_avg_spend: num(e.target.value, 20) })} /></div>
        </div>
        <div className="border border-emerald-300 bg-emerald-50 rounded-lg p-4 space-y-3">
          <div className="label-eyebrow text-emerald-900">Abituali</div>
          <div><Label>Visite minime</Label><Input data-testid="crm-abit-visits" type="number" value={form.abituale_min_visits ?? 10} onChange={(e) => setForm({ ...form, abituale_min_visits: num(e.target.value, 10) })} /></div>
          <div><Label>Giorni max dall'ultima visita</Label><Input data-testid="crm-abit-days" type="number" value={form.abituale_max_days_since_visit ?? 30} onChange={(e) => setForm({ ...form, abituale_max_days_since_visit: num(e.target.value, 30) })} /></div>
        </div>
        <div className="border border-sky-300 bg-sky-50 rounded-lg p-4 space-y-3">
          <div className="label-eyebrow text-sky-900">Nuovi</div>
          <div><Label>Giorni max dal primo acquisto</Label><Input data-testid="crm-nuovo-days" type="number" value={form.nuovo_max_days_since_first ?? 30} onChange={(e) => setForm({ ...form, nuovo_max_days_since_first: num(e.target.value, 30) })} /></div>
        </div>
        <div className="border border-red-300 bg-red-50 rounded-lg p-4 space-y-3">
          <div className="label-eyebrow text-red-900">Inattivi</div>
          <div><Label>Giorni minimi senza visite</Label><Input data-testid="crm-inatt-days" type="number" value={form.inattivo_min_days_since_visit ?? 60} onChange={(e) => setForm({ ...form, inattivo_min_days_since_visit: num(e.target.value, 60) })} /></div>
        </div>
      </div>
      <Button data-testid="save-crm" onClick={() => onSave(form)} className="bg-neutral-900 text-[#FDFBF7]"><Save size={14} strokeWidth={1.5} /> Salva regole CRM</Button>
    </div>
  );
}

function UsersTab({ settings }) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "personale" });
  const { user: me } = useAuth();
  const userRoles = settings?.lists?.user_roles || ["admin", "manager", "bar", "cucina", "personale", "commercialista"];

  const load = async () => { const { data } = await api.get("/users"); setUsers(data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Utente creato");
      setOpen(false);
      setForm({ email: "", password: "", name: "", role: "personale" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    }
  };

  const changeRole = async (id, role) => {
    await api.put(`/users/${id}`, { role });
    toast.success("Ruolo aggiornato"); load();
  };

  const resetPassword = async (id) => {
    const p = window.prompt("Nuova password (min 6 caratteri):");
    if (!p || p.length < 6) return;
    await api.put(`/users/${id}`, { password: p });
    toast.success("Password aggiornata");
  };

  const del = async (id, email) => {
    if (!window.confirm(`Eliminare l'utente ${email}?`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Utente eliminato"); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-neutral-500">{users.length} utenti registrati</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-user-btn" className="bg-neutral-900 text-[#FDFBF7]"><Plus size={14} strokeWidth={1.5} /> Nuovo utente</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif">Nuovo utente</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Nome e cognome</Label><Input data-testid="user-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Email</Label><Input data-testid="user-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Password (min 6)</Label><Input data-testid="user-password" type="text" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div><Label>Ruolo</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{userRoles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <DialogFooter><Button data-testid="user-submit" type="submit" className="bg-neutral-900 text-[#FDFBF7]">Crea</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-[#E2D9C8] rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm zebra">
          <thead><tr className="text-left label-eyebrow">
            <th className="p-3">Nome</th><th className="p-3">Email</th>
            <th className="p-3">Ruolo</th><th className="p-3">Creato</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[#E2D9C8]" data-testid={`user-${u.id}`}>
                <td className="p-3 font-medium">{u.name} {u.id === me?.id && <span className="text-xs text-neutral-500">(tu)</span>}</td>
                <td className="p-3 text-neutral-600">{u.email}</td>
                <td className="p-3">
                  <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                    <SelectTrigger className="h-8 w-40" data-testid={`role-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{userRoles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-3 text-xs text-neutral-500">{u.created_at?.slice(0, 10)}</td>
                <td className="p-3 text-right space-x-2">
                  <button onClick={() => resetPassword(u.id)} data-testid={`reset-${u.id}`} className="text-xs px-2 py-1 border border-[#E2D9C8] rounded hover:border-neutral-900">Reset password</button>
                  {u.id !== me?.id && (
                    <button onClick={() => del(u.id, u.email)} className="text-neutral-400 hover:text-red-700"><Trash2 size={14} strokeWidth={1.5} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AccountsTab({ settings }) {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "cassa", balance: 0, currency: "EUR" });
  const accountTypes = settings?.lists?.account_types?.length ? settings.lists.account_types : ["cassa", "banca", "POS", "carta", "altro"];

  const load = async () => {
    const { data } = await api.get("/accounts");
    setItems(data);
    setDrafts(Object.fromEntries(data.map((a) => [a.id, { name: a.name, type: a.type }])));
  };
  useEffect(() => { load(); }, []);

  const setField = (id, field, value) =>
    setDrafts((d) => ({ ...d, [id]: { ...d[id], [field]: value } }));

  const saveRow = async (id) => {
    try {
      await api.put(`/accounts/${id}`, drafts[id]);
      toast.success("Conto aggiornato");
      load();
    } catch { toast.error("Errore"); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Eliminare il conto "${name}"? Le transazioni collegate resteranno ma perderanno il riferimento al conto.`)) return;
    try { await api.delete(`/accounts/${id}`); toast.success("Conto eliminato"); load(); }
    catch { toast.error("Errore"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/accounts", { ...form, balance: Number(form.balance) });
      toast.success("Conto creato");
      setOpen(false);
      setForm({ name: "", type: "cash", balance: 0, currency: "EUR" });
      load();
    } catch { toast.error("Errore"); }
  };

  const totalBalance = items.reduce((s, a) => s + Number(a.balance || 0), 0);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-neutral-500">
          {items.length} conti · Saldo totale: <span className="font-medium text-neutral-900">€ {totalBalance.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-account-btn" className="bg-neutral-900 text-[#FDFBF7]"><Plus size={14} strokeWidth={1.5} /> Nuovo conto</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif">Nuovo conto</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Nome</Label><Input data-testid="acc-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Conto Corrente Banco di Sardegna" /></div>
              <div><Label>Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="acc-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {accountTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Saldo iniziale (€)</Label><Input type="number" step="0.01" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} /></div>
                <div><Label>Valuta</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></div>
              </div>
              <DialogFooter><Button data-testid="acc-submit" type="submit" className="bg-neutral-900 text-[#FDFBF7]">Crea</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-[#E2D9C8] rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm zebra">
          <thead><tr className="text-left label-eyebrow">
            <th className="p-3">Nome conto</th><th className="p-3 w-40">Tipo</th>
            <th className="p-3 text-right">Saldo attuale</th><th className="p-3 text-right w-52"></th>
          </tr></thead>
          <tbody>
            {items.map((a) => {
              const changed = drafts[a.id] && (drafts[a.id].name !== a.name || drafts[a.id].type !== a.type);
              return (
                <tr key={a.id} className="border-t border-[#E2D9C8]" data-testid={`acc-row-${a.id}`}>
                  <td className="p-3">
                    <Input data-testid={`acc-name-${a.id}`} value={drafts[a.id]?.name || ""}
                      onChange={(e) => setField(a.id, "name", e.target.value)}
                      className="max-w-md" />
                  </td>
                  <td className="p-3">
                    <Select value={drafts[a.id]?.type || a.type} onValueChange={(v) => setField(a.id, "type", v)}>
                      <SelectTrigger data-testid={`acc-type-${a.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {accountTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        {drafts[a.id]?.type && !accountTypes.includes(drafts[a.id].type) && (
                          <SelectItem value={drafts[a.id].type}>{drafts[a.id].type}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-3 text-right font-medium">€ {Number(a.balance || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="p-3 text-right space-x-2">
                    <Button size="sm" onClick={() => saveRow(a.id)} disabled={!changed}
                      data-testid={`save-acc-${a.id}`}
                      className={changed ? "bg-neutral-900 text-[#FDFBF7]" : ""}>
                      <Save size={12} strokeWidth={1.5} /> Salva
                    </Button>
                    <button onClick={() => del(a.id, a.name)} className="text-neutral-400 hover:text-red-700">
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-4 text-xs text-neutral-500 border-l-2 border-[#D7C4A3] pl-3">
        Il nome dei conti viene mostrato ovunque compaiano (Dashboard, Finanze, tendine dei movimenti). Il saldo si aggiorna automaticamente registrando movimenti; per un'inversione manuale registra un movimento di rettifica.
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, can } = useAuth();
  const [settings, setSettings] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/settings"); setSettings(data); }
    catch { toast.error("Errore caricamento impostazioni"); }
  };
  useEffect(() => { load(); }, []);

  // FASE 7.4 STEP E — permission-based access (no role hardcoding).
  if (user && !can("users.read")) {
    return (
      <div className="border border-red-300 bg-red-50 rounded-lg p-8 text-center" data-testid="admin-no-access">
        <Shield size={28} className="mx-auto text-red-700 mb-3" strokeWidth={1.5} />
        <h2 className="font-serif text-2xl">Accesso riservato</h2>
        <p className="text-sm text-red-800 mt-2">Non hai i permessi per accedere a questa sezione.</p>
      </div>
    );
  }

  const saveBusiness = async (form) => {
    try { const { data } = await api.put("/settings/business", form); setSettings(data); toast.success("Configurazione salvata"); }
    catch { toast.error("Errore"); }
  };
  const saveFinancial = async (form) => {
    try { const { data } = await api.put("/settings/financial", form); setSettings(data); toast.success("Parametri salvati"); }
    catch { toast.error("Errore"); }
  };
  const saveCrm = async (form) => {
    try { const { data } = await api.put("/settings/crm", form); setSettings(data); toast.success("Regole CRM salvate"); }
    catch { toast.error("Errore"); }
  };
  const saveList = async (key, values) => {
    try {
      await api.put(`/settings/lists/${key}`, { values });
      toast.success("Lista aggiornata");
      load();
    } catch { toast.error("Errore"); }
  };

  if (!settings) return <div className="text-neutral-500">Caricamento…</div>;

  return (
    <div data-testid="admin-page">
      <PageHeader eyebrow="Amministrazione" title="Impostazioni sistema"
        subtitle="Modifica dati attività, parametri economici, liste opzioni dei moduli e gestisci utenti." />

      <Tabs defaultValue="business" className="w-full">
        <div className="overflow-x-auto -mx-4 md:mx-0 px-4 md:px-0">
        <TabsList className="mb-6 bg-[#F3EDE1] p-1 flex flex-nowrap md:flex-wrap w-max md:w-auto">
          <TabsTrigger value="business" data-testid="tab-business"><Building2 size={14} strokeWidth={1.5} /> Attività</TabsTrigger>
          <TabsTrigger value="financial" data-testid="tab-financial"><Coins size={14} strokeWidth={1.5} /> Parametri</TabsTrigger>
          <TabsTrigger value="crm" data-testid="tab-crm"><Heart size={14} strokeWidth={1.5} /> CRM</TabsTrigger>
          <TabsTrigger value="accounts" data-testid="tab-accounts"><Wallet size={14} strokeWidth={1.5} /> Conti</TabsTrigger>
          <TabsTrigger value="lists" data-testid="tab-lists"><ListTree size={14} strokeWidth={1.5} /> Liste opzioni</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users"><Users size={14} strokeWidth={1.5} /> Utenti</TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="business">
          <div className="border border-[#E2D9C8] rounded-lg p-6 bg-white">
            <BusinessTab settings={settings} onSave={saveBusiness} />
          </div>
        </TabsContent>

        <TabsContent value="financial">
          <div className="border border-[#E2D9C8] rounded-lg p-6 bg-white">
            <FinancialTab settings={settings} onSave={saveFinancial} />
          </div>
        </TabsContent>

        <TabsContent value="crm">
          <div className="border border-[#E2D9C8] rounded-lg p-6 bg-white">
            <CrmTab settings={settings} onSave={saveCrm} />
          </div>
        </TabsContent>

        <TabsContent value="accounts">
          <AccountsTab settings={settings} />
        </TabsContent>

        <TabsContent value="lists">
          <div className="text-sm text-neutral-500 mb-4 border-l-2 border-[#D7C4A3] pl-4">
            Aggiungi o rimuovi le voci disponibili nei menu a tendina dei vari moduli (categorie prodotti, ruoli, stati, ecc.). Le modifiche sono immediate.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(settings.lists || {}).map(([k, vals]) => (
              <ChipsEditor key={k} listKey={k} values={vals} onSave={saveList} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="users">
          <UsersTab settings={settings} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
